import { tv } from "@ui/lib/tv";

/**
 * Shared chrome for the "search input + filtered ListBox of icon rows" pattern, built over
 * @ui/primitives/Autocomplete + @ui/primitives/ListBox by Select's popover,
 * StandardAutocomplete's torn-paper flyout, and the SearchBar Where panel.
 *
 * Each consumer owns a structurally different RAC composition (Select: selection state inside
 * its own popover machinery; StandardAutocomplete: controlled Autocomplete in an absolute
 * flyout; Where: a controlled SearchField with server-side filtering and two ListBoxes), so —
 * like SheetShell — this shares the option-list chrome at the slot level rather than forcing
 * one component. Consumers keep their bespoke surfaces (popover shell, torn paper, pill search
 * group) and extend slots via `className`.
 */
export const searchableListBox = tv({
  slots: {
    // SearchField is a grouping wrapper only; the visible box belongs to the consumer
    // (Field.Control, pill search group) unless the `boxed` field variant styles the input.
    searchField: "contents",
    searchInput: "text-neutral-900 outline-none placeholder:text-neutral-400",
    // Scrolling belongs to the ScrollArea this slot is handed to as a viewport —
    // it owns the overflow and the rail.
    list: "flex flex-col gap-1 outline-none",
    item: "flex cursor-pointer items-center rounded-full py-1 outline-none",
    itemIcon: "shrink-0",
    empty: "px-2 py-3 text-center text-sm text-neutral-400",
  },
  variants: {
    // How the search input gets its box.
    field: {
      // Transparent input laid into consumer-owned chrome.
      plain: { searchInput: "flex-1 bg-transparent caret-blue-400 text-base/6" },
      // The input is its own filled box (Select's popover search row).
      boxed: {
        searchField: "flex flex-col",
        searchInput: [
          "w-full rounded-lg bg-neutral-50 px-3 py-2",
          "focus:bg-white focus:shadow-[0_0_0_1px_var(--color-blue-400),0_0_0_3px_var(--color-blue-200)]",
        ],
      },
    },
    // Row icon treatment; also sets the row metrics that track the icon size.
    icon: {
      // Bare 16px glyph, neutral-tinted (StandardAutocomplete rows).
      sm: {
        item: "gap-2 px-2",
        itemIcon: "text-neutral-400 [&_svg]:size-4",
      },
      // 32px slot centring a 24px filled glyph (SearchBar rows).
      lg: {
        item: "gap-3 pl-2 pr-3",
        itemIcon: "flex size-8 items-center justify-center [&>svg]:size-6",
      },
    },
    // Keyboard-focus / hover tint on rows.
    tint: {
      subtle: { item: "data-[focused]:bg-neutral-50 data-[hovered]:bg-neutral-50" },
      solid: { item: "data-[focused]:bg-neutral-100" },
    },
  },
  defaultVariants: { field: "plain", icon: "lg", tint: "solid" },
});
